import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { createStore, updateStore } from "../../Apis/StoreAPI";

// Google Maps
import { GoogleMap, LoadScript, DrawingManager, Marker } from "@react-google-maps/api";

export const GOOGLE_MAPS_CONFIG = {
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCAPB0KIQ9hafwHNWsBj656sZNIPhoVtfQ',
    libraries: ['places', 'drawing', 'geometry'],
    id: 'google-map-script',
    version: 'weekly',
    region: 'US',
    language: 'en'
};

export const MAP_CONTAINER_STYLE = {
    width: '100%',
    height: '400px'
};

export const DEFAULT_CENTER = {
    lat: 17.3850,  // Hyderabad latitude
    lng: 78.4867   // Hyderabad longitude
};

const CreateStore = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const editMode = Boolean(id);
    const storeData = location.state?.store || null;

    const [name, setName] = useState("");
    const [iframeMap, setIframeMap] = useState("");
    const [street, setStreet] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [pincode, setPincode] = useState("");
    const [longitude, setLongitude] = useState("");
    const [latitude, setLatitude] = useState("");
    const [status, setStatus] = useState(true);
    const [storeImage, setStoreImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [radiusKm, setRadiusKm] = useState("");
    const [serviceAreaPolygon, setServiceAreaPolygon] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [markerPosition, setMarkerPosition] = useState(null);

    const polygonRef = useRef(null);

    useEffect(() => {
        if (editMode && storeData) {
            setName(storeData.name || "");
            setIframeMap(storeData.iframeMap || "");
            setStreet(storeData.street || "");
            setState(storeData.state || "");
            setCity(storeData.city || "");
            setPincode(storeData.pincode || "");
            setLongitude(storeData.longitude || "");
            setLatitude(storeData.latitude || "");
            setStatus(storeData.status ?? true);
            setPreview(storeData.image || null);
            setRadiusKm(storeData.radius_km || "");

            // Load polygon if exists
            if (storeData.service_area_polygon?.coordinates) {
                // Convert coordinates to [lat, lng]
                const converted = storeData.service_area_polygon.coordinates.map(ring =>
                    ring.map(([lng, lat]) => [lng, lat]) // ✅ keep GeoJSON format
                );
                setServiceAreaPolygon({ type: "Polygon", coordinates: converted });

            }

            if (storeData.longitude && storeData.latitude) {
                setMarkerPosition({ lat: parseFloat(storeData.latitude), lng: parseFloat(storeData.longitude) });
            }
        }
    }, [editMode, storeData]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setStoreImage(file);
        if (file) setPreview(URL.createObjectURL(file));
    };

    // Convert polygon drawn on map to GeoJSON Polygon with [lat, lng]
    const handlePolygonComplete = (polygon) => {
        const path = polygon.getPath().getArray().map(point => [point.lng(), point.lat()]); // fix order
        setServiceAreaPolygon({
            type: "Polygon",
            coordinates: [path] // wrap in array for GeoJSON
        });
        polygonRef.current = polygon;
    };

    const handleMapClick = (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });
        setLatitude(lat.toString());
        setLongitude(lng.toString());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !iframeMap || !street || !state || !city || !pincode || !longitude || !latitude) {
            setErrorMsg("Please fill all fields.");
            return;
        }

        const formData = new FormData();
        formData.append("name", name);
        formData.append("iframeMap", iframeMap);
        formData.append("street", street);
        formData.append("state", state);
        formData.append("city", city);
        formData.append("pincode", pincode);
        formData.append("longitude", longitude);
        formData.append("latitude", latitude);
        formData.append("status", status ? "true" : "false");
        formData.append("radius_km", radiusKm);

        // Always send GeoJSON polygon object, latitude first
        formData.append(
            "service_area_polygon",
            JSON.stringify(serviceAreaPolygon || { type: "Polygon", coordinates: [] })
        );

        if (storeImage) formData.append("storeimage", storeImage);

        try {
            setLoading(true);
            if (editMode) {
                await updateStore(id, formData);
            } else {
                await createStore(formData);
            }
            setSuccess(true);
            setTimeout(() => navigate("/getadminstores"), 1200);
        } catch (err) {
            setErrorMsg(editMode ? "Failed to update store." : "Failed to create store.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", px: 3, py: 4 }}>
            <Paper sx={{ width: "60%", p: 4, borderRadius: 3, boxShadow: 6 }}>
                <Typography variant="h5" gutterBottom>
                    {editMode ? "Update Store" : "Create Store"}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <TextField label="Store Name" value={name} onChange={e => setName(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="Map Iframe" value={iframeMap} onChange={e => setIframeMap(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="Street" value={street} onChange={e => setStreet(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="State" value={state} onChange={e => setState(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="City" value={city} onChange={e => setCity(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="Pincode" value={pincode} onChange={e => setPincode(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="Longitude" value={longitude} onChange={e => setLongitude(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="Latitude" value={latitude} onChange={e => setLatitude(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />
                        <TextField label="Radius (km)" value={radiusKm} onChange={e => setRadiusKm(e.target.value)} size="small" sx={{ flex: "1 1 48%" }} />

                        {/* <FormControl size="small" sx={{ flex: "1 1 48%" }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={status ? "true" : "false"} onChange={e => setStatus(e.target.value === "true")} label="Status">
                                <MenuItem value="true">Active</MenuItem>
                                <MenuItem value="false">Inactive</MenuItem>
                            </Select>
                        </FormControl> */}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={status}
                                    onChange={(e) => setStatus(e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Box>

                    <Button variant="contained" component="label" sx={{ mt: 2 }}>
                        Upload Image
                        <input type="file" hidden onChange={handleImageChange} />
                    </Button>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body1" gutterBottom>
                            Draw Service Area Polygon:
                        </Typography>
                        <LoadScript googleMapsApiKey={GOOGLE_MAPS_CONFIG.apiKey} libraries={GOOGLE_MAPS_CONFIG.libraries}>
                            <GoogleMap
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                center={markerPosition || DEFAULT_CENTER}
                                zoom={markerPosition ? 15 : 10}
                                onClick={handleMapClick}
                            >
                                {markerPosition && <Marker position={markerPosition} />}
                                <DrawingManager
                                    onPolygonComplete={handlePolygonComplete}
                                    options={{
                                        drawingControl: true,
                                        drawingControlOptions: { drawingModes: ["polygon"] },
                                        polygonOptions: { fillColor: "#2196F3", fillOpacity: 0.2, strokeWeight: 2, clickable: true, editable: true }
                                    }}
                                />
                            </GoogleMap>
                        </LoadScript>
                    </Box>

                    {preview && (
                        <Box sx={{ mt: 2, textAlign: "center" }}>
                            <Typography variant="body2">Image Preview:</Typography>
                            <img src={preview} alt="preview" style={{ width: 150, marginTop: 8, borderRadius: 8 }} />
                        </Box>
                    )}

                    <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 3 }} disabled={loading}>
                        {loading ? (editMode ? "Updating..." : "Creating...") : editMode ? "Update Store" : "Create Store"}
                    </Button>
                </form>

                <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                    <Alert severity="success" variant="filled" onClose={() => setSuccess(false)}>
                        {editMode ? "Store updated successfully!" : "Store created successfully!"}
                    </Alert>
                </Snackbar>

                <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                    <Alert severity="error" variant="filled" onClose={() => setErrorMsg("")}>
                        {errorMsg}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

export default CreateStore;
